import { type AnyAction } from "redux";
import { type ThunkAction } from "redux-thunk";
import { type MessageType } from "~/common/components/Messages/Message";
import { SELF_DISMISSABLE_TOASTS_TOP } from "~/utils/constants";
import { getNextUID } from "~/common/utils/uid";
import type { RootState } from "~/app/store";
import { type GlobalMessageTextType, type MessageData } from "~/features/toasts/redux/types";

import { type TranslationModel, TranslationType } from "~/common/types/TranslationModel";

export const ADD_MSG = "globalMessages: ADD";
export const SET_MSG = "globalMessages: SET";
export const CLEAR_MSG = "globalMessages: CLEAR";
export const DISSMISS_MSG = "globalMessages: DISSMISS";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function addMessage(severity: MessageType, message: string, type: GlobalMessageTextType = "raw") {
  return addMessages([
    {
      severity,
      message: {
        type: type === "raw" ? TranslationType.RAW_TEXT : TranslationType.INTERNAL,
        value: message,
      } as TranslationModel,
    },
  ]);
}
export function addMessageAdvanced(severity: MessageType, message: TranslationModel) {
  return addMessages([
    {
      severity,
      message,
    },
  ]);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function addMessages(msgs: { severity: MessageType; message: TranslationModel }[]): ThunkResult<any> {
  return (dispatch, getState) => {
    const messagesWithId = msgs.map((msg) => {
      const id = getNextUID();
      // self dismissable toast for INFO type
      if ("info" === msg.severity) {
        window.setTimeout(() => dispatch(dismissMessage(id)), SELF_DISMISSABLE_TOASTS_TOP);
      }
      return { message: msg.message, severity: msg.severity, id };
    });
    return dispatch(addMessagesAction(messagesWithId));
  };
}

function addMessagesAction(msgs: MessageData[]) {
  return {
    type: ADD_MSG,
    payload: msgs,
  } as const;
}

export function setMessageAction(severity: MessageType, message: string, type: GlobalMessageTextType = "raw") {
  return {
    type: SET_MSG,
    payload: [
      {
        severity,
        message: {
          type: type === "raw" ? TranslationType.RAW_TEXT : TranslationType.INTERNAL,
          value: message,
        } as TranslationModel,
        type,
        id: getNextUID(),
      },
    ],
  } as const;
}

export function setMessageActionAdvanced(severity: MessageType, message: TranslationModel) {
  return {
    type: SET_MSG,
    payload: [
      {
        severity,
        message,
        id: getNextUID(),
      },
    ],
  } as const;
}

export function clearMessages() {
  return {
    type: CLEAR_MSG,
  } as const;
}

export function dismissMessage(id: number) {
  return {
    type: DISSMISS_MSG,
    payload: id,
  } as const;
}

export type GlobalMessageActions =
  | ReturnType<typeof addMessagesAction>
  | ReturnType<typeof setMessageAction>
  | ReturnType<typeof clearMessages>
  | ReturnType<typeof dismissMessage>;
